import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiClient } from '../lib/apiClient';
import { 
  Shield, Search, Filter, Download, RefreshCw, 
  ChevronLeft, ChevronRight, ChevronDown, AlertCircle,
  CheckCircle, XCircle, User, Settings, Package, FileText, 
  Users, Trash2, Clock, Eye, ToggleLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

// ── Action config ────────────────────────────────────────────────────────────
const ACTION_CONFIG = {
  'registration.approved':      { label: 'Kayıt Onaylandı',           color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle  },
  'registration.rejected':      { label: 'Kayıt Reddedildi',          color: 'bg-red-100 text-red-700',        icon: XCircle      },
  'registration.bulk_approved': { label: 'Toplu Onay',                color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle  },
  'registration.bulk_rejected': { label: 'Toplu Ret',                 color: 'bg-red-100 text-red-700',        icon: XCircle      },
  'tenant.created':             { label: 'Tenant Oluşturuldu',        color: 'bg-blue-100 text-blue-700',      icon: Users        },
  'tenant.updated':             { label: 'Tenant Güncellendi',        color: 'bg-blue-100 text-blue-700',      icon: Users        },
  'tenant.deleted':             { label: 'Tenant Silindi',            color: 'bg-red-100 text-red-700',        icon: Trash2       },
  'user.created':               { label: 'Kullanıcı Oluşturuldu',     color: 'bg-purple-100 text-purple-700',  icon: User         },
  'user.deleted':               { label: 'Kullanıcı Silindi',         color: 'bg-red-100 text-red-700',        icon: Trash2       },
  'user.deactivated':           { label: 'Kullanıcı Pasife Alındı',   color: 'bg-amber-100 text-amber-700',    icon: ToggleLeft   },
  'contract.sent':              { label: 'Sözleşme Gönderildi',       color: 'bg-indigo-100 text-indigo-700',  icon: FileText     },
  'stand.assigned':             { label: 'Stand Atandı',              color: 'bg-cyan-100 text-cyan-700',      icon: Package      },
  'stand.updated':              { label: 'Stand Güncellendi',         color: 'bg-cyan-100 text-cyan-700',      icon: Package      },
  'settings.platform_updated':  { label: 'Platform Ayarları',         color: 'bg-gray-100 text-gray-700',      icon: Settings     },
  'settings.shopping_updated':  { label: 'Alışveriş Ayarları',        color: 'bg-gray-100 text-gray-700',      icon: Settings     },
  'settings.session_updated':   { label: 'Oturum Ayarları',           color: 'bg-gray-100 text-gray-700',      icon: Clock        },
  'product.created':            { label: 'Ürün Oluşturuldu',          color: 'bg-orange-100 text-orange-700',  icon: Package      },
  'product.updated':            { label: 'Ürün Güncellendi',          color: 'bg-orange-100 text-orange-700',  icon: Package      },
  'product.deleted':            { label: 'Ürün Silindi',              color: 'bg-red-100 text-red-700',        icon: Trash2       },
  // ── Phase 86AT/86AY — Green Zone manual onboarding ────────────────────────
  'user.green_zone_manual_create':         { label: 'Green Zone Müşterisi Oluşturuldu',  color: 'bg-emerald-100 text-emerald-700', icon: User       },
  'user.green_zone_invite_sent':           { label: 'Green Zone Daveti Gönderildi',      color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle},
  'user.green_zone_invite_failed':         { label: 'Green Zone Daveti Gönderilemedi',   color: 'bg-red-100 text-red-700',         icon: XCircle    },
  // ── Phase 86AZ.58 — Manual supplier onboarding ────────────────────────────
  'user.supplier_manual_create':           { label: 'Tedarikçi Oluşturuldu',             color: 'bg-amber-100 text-amber-700',     icon: User       },
  'user.supplier_invite_resent':           { label: 'Tedarikçi Daveti Yeniden Gönderildi', color: 'bg-amber-100 text-amber-700',   icon: CheckCircle},
  'user.supplier_invite_resend_failed':    { label: 'Tedarikçi Daveti Gönderilemedi',    color: 'bg-red-100 text-red-700',         icon: XCircle    },
  'supplier.password_set':                 { label: 'Tedarikçi Şifresini Belirledi',     color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle},
  // ── Phase 86AZ.59 — Manual contractor onboarding ──────────────────────────
  'user.contractor_manual_create':         { label: 'Müteahhit Oluşturuldu',             color: 'bg-red-100 text-red-700',         icon: User       },
  'user.contractor_invite_resent':         { label: 'Müteahhit Daveti Yeniden Gönderildi', color: 'bg-red-100 text-red-700',       icon: CheckCircle},
  'user.contractor_invite_resend_failed':  { label: 'Müteahhit Daveti Gönderilemedi',    color: 'bg-red-100 text-red-700',         icon: XCircle    },
  'contractor.password_set':               { label: 'Müteahhit Şifresini Belirledi',     color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle},
};

const TARGET_TYPES = ['registration', 'tenant', 'user', 'contract', 'stand', 'settings', 'product'];

function ActionBadge({ action }) {
  const cfg = ACTION_CONFIG[action] || { label: action, color: 'bg-gray-100 text-gray-600', icon: Shield };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function AuditLogsPage() {
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // Filters
  const [search, setSearch]         = useState('');
  const [actionFilter, setAction]   = useState('');
  const [targetType, setTargetType] = useState('');
  const [startDate, setStartDate]   = useState('');
  const [endDate, setEndDate]       = useState('');
  const [page, setPage]             = useState(1);
  const LIMIT = 50;

  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (search)      params.append('search', search);
      if (actionFilter) params.append('action_type', actionFilter);
      if (targetType)  params.append('target_type', targetType);
      if (startDate)   params.append('start_date', startDate);
      if (endDate)     params.append('end_date', endDate);

      const r = await apiClient.get(`/ultra-admin/audit-logs?${params}`);
      setLogs(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch (e) {
      setError(e.response?.data?.detail || (tr ? 'Log yüklenemedi.' : 'Failed to load logs.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, actionFilter, targetType, startDate, endDate, tr]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // CSV export
  const handleExport = () => {
    const header = ['Tarih', 'Aktör', 'Rol', 'Aksiyon', 'Hedef Tipi', 'Hedef', 'Detaylar'];
    const rows = logs.map(l => [
      formatDate(l.created_at),
      l.actor_email,
      l.actor_role,
      l.action,
      l.target_type,
      l.target_name,
      JSON.stringify(l.details || {}),
    ]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setSearch(''); setAction(''); setTargetType('');
    setStartDate(''); setEndDate(''); setPage(1);
  };

  const hasFilters = search || actionFilter || targetType || startDate || endDate;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-8 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gray-900 flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{tr ? 'Audit Log' : 'Audit Log'}</h1>
              <p className="text-sm text-gray-400">
                {tr ? `Toplam ${total.toLocaleString()} kayıt` : `${total.toLocaleString()} total entries`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading} className="gap-2 rounded-full">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {tr ? 'Yenile' : 'Refresh'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={logs.length === 0} className="gap-2 rounded-full">
              <Download className="h-4 w-4" />
              CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">
        {/* Filter Bar */}
        <Card className="rounded-2xl shadow-sm border-gray-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  data-testid="audit-search"
                  placeholder={tr ? 'Aktör, aksiyon veya hedef ara...' : 'Search actor, action, or target...'}
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  className="pl-9 rounded-xl"
                />
              </div>

              {/* Action Type */}
              <div className="relative min-w-[180px]">
                <select
                  data-testid="audit-action-filter"
                  value={actionFilter}
                  onChange={e => { setAction(e.target.value); setPage(1); }}
                  className="w-full h-10 pl-3 pr-8 border border-gray-200 rounded-xl text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-gray-900"
                >
                  <option value="">{tr ? 'Tüm Aksiyonlar' : 'All Actions'}</option>
                  {Object.entries(ACTION_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Target Type */}
              <div className="relative min-w-[140px]">
                <select
                  value={targetType}
                  onChange={e => { setTargetType(e.target.value); setPage(1); }}
                  className="w-full h-10 pl-3 pr-8 border border-gray-200 rounded-xl text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-gray-900"
                >
                  <option value="">{tr ? 'Tüm Tipler' : 'All Types'}</option>
                  {TARGET_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Date Range */}
              <Input
                type="date"
                value={startDate}
                onChange={e => { setStartDate(e.target.value); setPage(1); }}
                className="w-36 rounded-xl"
                placeholder="Başlangıç"
              />
              <span className="text-gray-400 text-sm">—</span>
              <Input
                type="date"
                value={endDate}
                onChange={e => { setEndDate(e.target.value); setPage(1); }}
                className="w-36 rounded-xl"
                placeholder="Bitiş"
              />

              {/* Reset */}
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-1 text-gray-500 rounded-full">
                  <Filter className="h-4 w-4" />
                  {tr ? 'Temizle' : 'Clear'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <div className="bg-red-50 rounded-xl p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Table */}
        <Card className="rounded-2xl shadow-sm border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin h-8 w-8 border-4 border-gray-900 border-t-transparent rounded-full" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
              <Shield className="h-12 w-12 opacity-30" />
              <p className="text-sm">{tr ? 'Henüz kayıt bulunamadı.' : 'No audit logs found yet.'}</p>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs">
                  {tr ? 'Filtreleri temizle' : 'Clear filters'}
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {[tr ? 'Tarih & Saat' : 'Date & Time', tr ? 'Aktör' : 'Actor', tr ? 'Aksiyon' : 'Action', tr ? 'Hedef' : 'Target', tr ? 'Detay' : 'Detail'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {logs.map((log, i) => (
                    <tr key={log.id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
                          <span className="text-xs text-gray-600 font-mono">{formatDate(log.created_at)}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0">
                            <User className="h-3.5 w-3.5 text-white" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900 truncate max-w-[180px]">{log.actor_email}</p>
                            <p className="text-xs text-gray-400">{log.actor_role?.replace('_', ' ')}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <ActionBadge action={log.action} />
                      </td>
                      <td className="px-5 py-3.5">
                        <div>
                          <p className="text-xs font-medium text-gray-900 truncate max-w-[200px]">{log.target_name || log.target_id || '-'}</p>
                          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{log.target_type}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <button
                            data-testid={`audit-detail-btn-${i}`}
                            onClick={() => setSelectedLog(log)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                          >
                            <Eye className="h-3 w-3" />
                            {tr ? 'Görüntüle' : 'View'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {tr ? `${((page - 1) * LIMIT) + 1}–${Math.min(page * LIMIT, total)} / ${total} kayıt` : `${((page - 1) * LIMIT) + 1}–${Math.min(page * LIMIT, total)} of ${total}`}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-full gap-1">
                <ChevronLeft className="h-4 w-4" />
                {tr ? 'Önceki' : 'Prev'}
              </Button>
              <span className="text-sm font-medium text-gray-700 px-2">{page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page === pages} onClick={() => setPage(p => p + 1)} className="rounded-full gap-1">
                {tr ? 'Sonraki' : 'Next'}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg rounded-2xl shadow-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900">{tr ? 'Aksiyon Detayı' : 'Action Detail'}</h3>
                <button onClick={() => setSelectedLog(null)} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <ActionBadge action={selectedLog.action} />
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-gray-400">{tr ? 'Aktör' : 'Actor'}</p><p className="font-medium">{selectedLog.actor_email}</p></div>
                  <div><p className="text-xs text-gray-400">{tr ? 'Tarih' : 'Date'}</p><p className="font-medium">{formatDate(selectedLog.created_at)}</p></div>
                  <div><p className="text-xs text-gray-400">{tr ? 'Hedef' : 'Target'}</p><p className="font-medium">{selectedLog.target_name || selectedLog.target_id}</p></div>
                  <div><p className="text-xs text-gray-400">{tr ? 'Tip' : 'Type'}</p><p className="font-medium">{selectedLog.target_type}</p></div>
                </div>
                {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 mb-2">{tr ? 'Detaylar' : 'Details'}</p>
                    <pre className="bg-gray-50 rounded-xl p-3 text-xs text-gray-700 overflow-auto max-h-48 font-mono">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
              <Button onClick={() => setSelectedLog(null)} className="w-full rounded-full bg-gray-900 hover:bg-gray-800 text-white">
                {tr ? 'Kapat' : 'Close'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
